<div class="hx-icd-lookup-trigger">
  <button
    #trigger
    aria-haspopup="dialog"
    hxIconButton
    type="button"
    [ariaLabel]="buttonAriaLabel()"
    [attr.aria-expanded]="isPanelOpen()"
    [attr.title]="buttonTooltip() ?? buttonAriaLabel()"
    [context]="buttonContext()"
    [disabled]="disabled()"
    [hxTooltip]="buttonTooltip() ?? buttonAriaLabel()"
    [iconSize]="iconSize()"
    [rounded]="buttonRounded()"
    [size]="buttonSize()"
    [svgIcon]="buttonIcon()"
    [variant]="buttonVariant()"
    (click)="openPanel()"></button>
</div>

<ng-template cdkPortal>
  <div class="hx-icd-lookup__overlay" role="presentation" (click)="onOverlayClick($event)">
    <hx-side-nav
      class="hx-icd-lookup__side-nav"
      mode="over"
      position="end"
      [animationDuration]="0"
      [autoFocus]="false"
      [disableClose]="true"
      [opened]="true"
      [showHeader]="false"
      [showToggle]="false">
      <div
        #panel
        aria-modal="true"
        class="hx-icd-lookup__panel"
        role="dialog"
        tabindex="-1"
        [attr.aria-labelledby]="panelId + '-search-title'"
        [cdkTrapFocus]="!pendingSource()"
        [stopPropagation]="['click']">
        <div class="hx-icd-lookup__layout" [attr.inert]="pendingSource() ? '' : null">
          <section class="hx-icd-lookup__sources" [attr.aria-labelledby]="panelId + '-sources-title'">
            <header class="hx-icd-lookup__pane-header">
              <h2 [id]="panelId + '-sources-title'">Assessment &amp; Plan codes</h2>
            </header>
            <div class="hx-icd-lookup__source-list hx-icd-lookup__scroll">
              @for (group of sourceGroups(); track group.id) {
                <section class="hx-icd-lookup__source-group" [attr.aria-label]="group.sectionTitle">
                  <h3 class="hx-icd-lookup__breadcrumb">
                    <span>{{ group.sectionTitle }}</span>
                    @if (group.subsectionTitle) {
                      <hx-icon aria-hidden="true" svgIcon="chevron-right" />
                      <span>{{ group.subsectionTitle }}</span>
                    }
                  </h3>
                  <ul class="hx-icd-lookup__code-list">
                    @for (code of group.codes; track code.code) {
                      <li class="hx-icd-lookup__source-code">
                        <span>{{ code.code }} - {{ code.description }}</span>
                        <button
                          context="brand"
                          hxIconButton
                          size="sm"
                          svgIcon="arrow-up-right"
                          type="button"
                          variant="ghost"
                          [ariaLabel]="
                            'Go to ' +
                            group.sectionTitle +
                            (group.subsectionTitle ? ', ' + group.subsectionTitle : '') +
                            ' for ' +
                            code.code
                          "
                          [attr.title]="'Go to source for ' + code.code"
                          (click)="requestGoToSource(code.sourceRef)"></button>
                      </li>
                    }
                  </ul>
                </section>
              } @empty {
                <p class="hx-icd-lookup__empty">No ICD codes have been added from assessment sections yet.</p>
              }
            </div>
          </section>

          <section class="hx-icd-lookup__search" [attr.aria-labelledby]="panelId + '-search-title'">
            <header class="hx-icd-lookup__pane-header">
              <h2 [id]="panelId + '-search-title'">{{ title() }}</h2>
              <button
                ariaLabel="Close ICD lookup"
                context="neutral"
                hxIconButton
                size="sm"
                svgIcon="ccs-x"
                title="Close ICD lookup"
                type="button"
                variant="ghost"
                (click)="onCancel()"></button>
            </header>
            <div class="hx-icd-lookup__search-body hx-icd-lookup__scroll">
              <p class="hx-icd-lookup__help" [id]="panelId + '-search-help'">
                Search and select codes, then choose Add to include them in Assessment &amp; Plan.
              </p>
              <hx-search-typeahead
                data-testid="icd-search-input"
                expandedCount="all"
                label="Search ICD codes"
                panelClass="hx-icd-lookup-search-panel"
                [enableIncrementalLoading]="enableIncrementalLoading()"
                [externalSearch]="externalSearch()"
                [hasMore]="hasMore()"
                [items]="searchItems()"
                [loadingMore]="loadingMore()"
                [minChars]="0"
                [multiple]="true"
                [pageSize]="pageSize()"
                [placeholder]="placeholder()"
                [showTags]="true"
                (itemsChanged)="onItemsChanged($event)"
                (loadMore)="loadMore.emit($event)"
                (searchChange)="searchChange.emit($event)"
                (selectedItemsChanged)="onSelectedItemsChanged($event)" />
              @if (selectedCodes().length > 0) {
                <p class="hx-icd-lookup__selection-summary" role="status">
                  {{ selectedCodes().length }} selected to add
                </p>
              }
              <hx-accordion class="hx-icd-lookup__committed">
                <hx-accordion-panel labelSize="small" [expanded]="true">
                  <div hxAccordionHeader>Already selected ICDs ({{ alreadySelectedCodes().length }})</div>
                  <div hxAccordionContent>
                    <p class="hx-icd-lookup__help">Previously added through ICD Search.</p>
                    <ul class="hx-icd-lookup__tags">
                      @for (code of alreadySelectedCodes(); track code.code) {
                        <li>
                          <hx-tag
                            color="primary"
                            size="sm"
                            variant="outlined"
                            [label]="code.code + ' - ' + code.description"
                            [maxWidth]="undefined"
                            [removable]="false" />
                        </li>
                      } @empty {
                        <li class="hx-icd-lookup__empty">No codes have been added through ICD Search yet.</li>
                      }
                    </ul>
                  </div>
                </hx-accordion-panel>
              </hx-accordion>
            </div>
          </section>
        </div>

        <footer class="hx-icd-lookup__actions" [attr.inert]="pendingSource() ? '' : null">
          <button
            context="neutral"
            data-testid="cancel-button"
            hxButton
            type="button"
            variant="outline"
            (click)="onCancel()">
            Cancel
          </button>
          <button
            context="brand"
            data-testid="add-button"
            hxButton
            type="button"
            variant="fill"
            [disabled]="!isAddButtonEnabled()"
            (click)="onAdd()">
            Add
          </button>
        </footer>

        @if (pendingSource()) {
          <div cdkTrapFocus class="hx-icd-lookup__confirmation" [cdkTrapFocusAutoCapture]="true">
            <hx-confirmation-popup
              acceptLabel="Discard and navigate"
              cancelLabel="Keep selecting"
              headerText="Discard selected ICD codes?"
              message="Your pending selections have not been added. Discard them and go to the source section?"
              variant="warning"
              (accept)="confirmNavigation()"
              (cancel)="pendingSource.set(null)" />
          </div>
        }
      </div>
    </hx-side-nav>
  </div>
</ng-template>
